import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getUserDefaultLandingPage, setUserDefaultLandingPage, getAccessibleLandingPageOptions } from '../../utils/userSettings';
import axiosInstance from '../../services/AxiosInstance';
import { accessControlService } from '../../services/accessControlService';

const UserSettings = () => {
  const [landingPage, setLandingPage] = useState('/task/tasklist');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [corporateUserId, setCorporateUserId] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);

  // Landing page options - will be filtered based on access
  const [landingPageOptions, setLandingPageOptions] = useState([]);

  // Toast notification functions
  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  // Get current user data and corporate user ID
  useEffect(() => {
    const getUserData = () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        if (userData && userData.id) {
          setCorporateUserId(userData.id);
          setUserRole(userData.role);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        notifyError('Failed to load user data');
      }
    };
    
    getUserData();
  }, []);

  // Fetch Access Control and filter landing page options (only show accessible pages)
  useEffect(() => {
    const fetchAccessibleOptions = async () => {
      setAccessLoading(true);
      try {
        const res = await accessControlService.getMyAccess();
        const routePaths = res?.data?.data?.routePaths || [];
        const filteredOptions = getAccessibleLandingPageOptions(routePaths);
        setLandingPageOptions(filteredOptions);
        
        // If current landing page is not accessible, reset to first available or task
        if (landingPage && !filteredOptions.some((option) => option.value === landingPage)) {
          setLandingPage(filteredOptions.length > 0 ? filteredOptions[0].value : '/task/tasklist');
        }
      } catch (error) {
        console.error('Error fetching access control:', error);
        setLandingPageOptions([{ value: '/task/tasklist', label: 'Task' }]);
      } finally {
        setAccessLoading(false);
      }
    };

    fetchAccessibleOptions();
  }, [corporateUserId, landingPage]);

  // Load user settings from API
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!corporateUserId) return;
      
      setInitialLoading(true);
      try {
        const response = await axiosInstance.get(`/settings/corporate-user-settings/${corporateUserId}`);
        const data = response.data;
        
        if (data && data.defaultLandingPage) {
          setLandingPage(data.defaultLandingPage);
        } else {
          // Fallback to localStorage if no API data
          const savedLandingPage = getUserDefaultLandingPage();
          setLandingPage(savedLandingPage);
        }
      } catch (error) {
        console.error('Error fetching user settings:', error);
        // Fallback to localStorage on API error
        const savedLandingPage = getUserDefaultLandingPage();
        setLandingPage(savedLandingPage);
        
        // Only show error if it's not a 404 (user settings might not exist yet)
        if (error.response?.status !== 404) {
          notifyError('Failed to load user settings. Using local settings.');
        }
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUserSettings();
  }, [corporateUserId]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save to backend API
      const response = await axiosInstance.put('/settings/corporate-user-settings', {
        corporate_user_id: corporateUserId,
        default_landing_page: landingPage
      });
      
      // Also save to localStorage as backup
      setUserDefaultLandingPage(landingPage);
      
      // Show success message
      notifyTopRight('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      
      // Fallback to localStorage if API fails
      try {
        setUserDefaultLandingPage(landingPage);
        notifyTopRight('Settings saved locally. Please check your connection.');
      } catch (localError) {
        console.error('Error saving to localStorage:', localError);
        notifyError('Failed to save settings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle landing page change
  const handleLandingPageChange = (e) => {
    setLandingPage(e.target.value);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col lg={8}>
            <Card>
              <Card.Header>
                <h4 className="card-title mb-0">Session Default Landing Page</h4>
              </Card.Header>
                <Card.Body>
                  {!corporateUserId ? (
                    <Alert variant="warning" className="mb-3">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Unable to load user data. Please refresh the page or log in again.
                    </Alert>
                  ) : initialLoading || accessLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">
                        {initialLoading ? 'Loading user settings...' : 'Loading accessible pages...'}
                      </p>
                    </div>
                  ) : (
                    <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="landingPage">
                          <strong>Default Landing Page</strong>
                        </Form.Label>
                        <Form.Select
                          id="landingPage"
                          value={landingPage}
                          onChange={handleLandingPageChange}
                          required
                          disabled={!corporateUserId || accessLoading}
                        >
                          {landingPageOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          This page will be displayed when you first log in or navigate to the home page.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || !corporateUserId || accessLoading}
                      className="btn btn-primary"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Settings
                        </>
                      )}
                    </Button>
                    </div>
                  </Form>
                  )}
                </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
      <ToastContainer />
    </div>
  );
};

export default UserSettings;
